import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CfeDocument, CfePendingItem } from '@/types/cfe';
import dgiService from '@/services/dgiService';
import { formatReceiptAmount } from '@/utils/currencyUtils';
import { formatLocalDate } from '@/utils/localDateUtils';
import { formatDgiReason, getCfeRequestError } from '@/utils/cfeUtils';
import DataTable, { Column } from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import CfeStatusBadge from './CfeStatusBadge';
import CfeDetailModal from './CfeDetailModal';

export default function ResguardoStatusPanel() {
  const { hasPermission } = useAuth();
  const [filter, setFilter] = useState('TODOS');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<CfePendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<CfeDocument | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState('');
  const stop = useRef(false);
  const mounted = useRef(true);
  const requestVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    try {
      const response = await dgiService.getResguardos(filter, page);
      if (!mounted.current || version !== requestVersion.current) return;
      setRows(response.data.rows);
      setTotal(response.data.total);
      setPage(response.data.page);
    } catch (error) {
      if (mounted.current && version === requestVersion.current) setError(getCfeRequestError(error, 'No se pudieron cargar los resguardos'));
    } finally {
      if (mounted.current && version === requestVersion.current) setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; stop.current = true; requestVersion.current++; };
  }, []);
  useEffect(() => { void load(); }, [load]);

  const synchronize = async () => {
    if (syncing) return;
    setSyncing(true);
    stop.current = false;
    setError('');
    let afterId = 0;
    let upToId: number | undefined;
    let checked = 0;
    let rejected = 0;
    let failed = 0;
    try {
      do {
        const { data } = await dgiService.syncResguardos(afterId, upToId);
        if (!mounted.current) return;
        if (data.result) {
          checked++;
          if (!data.result.success) failed++;
          if (['RECHAZADO', 'RECHAZADO_O_ANULADO'].includes(data.result.estado || '')) rejected++;
        }
        setProgress(`${checked} consultados · ${rejected} rechazados o anulados · ${failed} consultas fallidas · ${data.remaining} pendientes de consultar`);
        if (data.done) break;
        if (data.after_id <= afterId) throw new Error('La consulta no pudo avanzar');
        afterId = data.after_id;
        upToId = data.up_to_id;
      } while (!stop.current);
    } catch (error) {
      if (mounted.current) setError(getCfeRequestError(error, 'La actualización se interrumpió. Los resultados obtenidos se conservaron'));
    } finally {
      if (mounted.current) {
        setSyncing(false);
        // Refresh the list without discarding an error from the synchronization request.
        const response = await dgiService.getResguardos(filter, page).catch(() => null);
        if (mounted.current && response) {
          setRows(response.data.rows);
          setTotal(response.data.total);
        }
      }
    }
  };

  const columns: Column<CfePendingItem>[] = [
    { key: 'cfe_numero', label: 'Resguardo', render: row => `${row.cfe_serie || '-'}-${row.cfe_numero || '-'}` },
    { key: 'numero_recibo', label: 'Recibo' },
    { key: 'receptor_nombre', label: 'Receptor' },
    { key: 'monto_total', label: 'Importe', render: row => formatReceiptAmount(row.monto_total, row.moneda) },
    { key: 'dgi_estado', label: 'Estado DGI', render: row => <CfeStatusBadge cfe={row} showType={false} /> },
    { key: 'repair_replacement_number', label: 'Reparación', render: row => row.repair_replacement_id ? `${row.repair_original_number} → ${row.repair_replacement_number} · ${row.repair_phase === 'ACEPTADA' ? 'Completada' : 'Pendiente'}` : '-' },
    { key: 'dgi_motivo_rechazo', label: 'Motivo', render: row => <span className="text-sm whitespace-pre-wrap">{formatDgiReason(row.dgi_motivo_rechazo) || '-'}</span> },
    { key: 'indicador_anulacion', label: 'Indicador 9 en XML', render: row => Number(row.indicador_anulacion) === 1 ? 'Sí' : 'No' },
    { key: 'dgi_fecha_consulta', label: 'Última consulta exitosa', render: row => row.dgi_fecha_consulta ? formatLocalDate(row.dgi_fecha_consulta, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-' },
    { key: 'dgi_error_consulta', label: 'Error de consulta', render: row => row.dgi_error_consulta || '-' },
  ];

  return <div className="space-y-4">
    <p className="text-sm text-gray-600">Consultá el resultado en Taface para identificar resguardos rechazados. Actualizar estados no emite ni reenvía comprobantes.</p>
    <div className="flex flex-wrap gap-3 items-center">
      <label className="text-sm">Estado DGI
        <select aria-label="Estado DGI" value={filter} disabled={syncing} className="ml-2 border rounded p-2"
          onChange={event => { setFilter(event.target.value); setPage(1); }}>
          <option value="TODOS">Todos</option><option value="RECHAZADO">Rechazados</option>
          <option value="E05">Rechazados con E05</option><option value="SIN_CONSULTAR">Sin consultar</option>
          <option value="PENDIENTE">Pendientes de respuesta</option><option value="ACEPTADO">Aceptados</option>
          <option value="ACEPTADO_O_NC">Aceptados o N/C</option>
          <option value="ANULADO">Anulados</option><option value="RECHAZADO_O_ANULADO">Rechazados o anulados</option>
          <option value="DESCONOCIDO">Estado sin identificar</option>
        </select>
      </label>
      {hasPermission('dgi', 'write') && <Button onClick={synchronize} disabled={syncing}>Actualizar todos los resguardos enviados</Button>}
      {syncing && <Button variant="secondary" onClick={() => { stop.current = true; }}>Detener al terminar la consulta actual</Button>}
    </div>
    {progress && <p role="status" className="text-sm text-gray-600">{progress}</p>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    <p className="text-sm">{total} resguardos en este filtro. La búsqueda se aplica a la página actual.</p>
    <DataTable columns={columns} data={rows} loading={loading} rowKey="id" searchable
      searchFields={['numero_recibo', 'receptor_nombre', 'cfe_numero', 'dgi_motivo_rechazo']}
      searchPlaceholder="Buscar en esta página..." onRowClick={async row => {
        try {
          const response = await dgiService.getDocument(Number(row.id));
          if (mounted.current) setSelected(response.data);
        } catch {
          if (mounted.current) setError('No se pudo cargar el detalle del resguardo.');
        }
      }}
      emptyState={<p className="p-6 text-gray-500">No hay resguardos para este filtro.</p>} />
    <div className="flex items-center gap-3">
      <Button variant="secondary" disabled={page === 1 || syncing || loading} onClick={() => setPage(value => value - 1)}>Anterior</Button>
      <span>Página {page} de {Math.max(1, Math.ceil(total / 50))}</span>
      <Button variant="secondary" disabled={page * 50 >= total || syncing || loading} onClick={() => setPage(value => value + 1)}>Siguiente</Button>
    </div>
    <CfeDetailModal isOpen={selected !== null} cfe={selected} onClose={() => setSelected(null)}
      onCfeUpdated={() => { setSelected(null); void load(); }} />
  </div>;
}
